import { useState, useEffect } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { LockClosedIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline'

import ModalContainer from 'components/ModalContainer'
import { Button, Card, Input, MultiButton, Dropdown, Checkbox } from 'components/gsys-ui'
import haxios, { getToken } from 'util/haxios'
import { v4 as uuidv4 } from 'uuid'

const permMultiOpt = [
  {
    label: 'None',
    value: 0
  },
  {
    label: 'Read only',
    value: 1
  },
  {
    label: 'Full',
    value: 2
  }
]

const AddUserModal = NiceModal.create(({ refreshList }) => {
  const modal = useModal()
  const [fname, setFname] = useState('')
  const [lname, setLname] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tmaPermMulti, setTmaPermMulti] = useState(0)
  const [vmobPermMulti, setVmobPermMulti] = useState(0)
  const [cplusPermMulti, setCplusPermMulti] = useState(0)
  const [planPermMulti, setPlanPermMulti] = useState(0)
  const [branch, setBranch] = useState(null)
  const [admin, setAdmin] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    setLoading(true)
    const uuid = uuidv4()

    try {
      await haxios.post(`${process.env.REACT_APP_BASE_URL}/api/users/portal`, {
        UserID: uuid,
        FName: fname,
        SName: lname,
        Username: username,
        Password: password,
        Email: email,
        UserConnectPlusAccess: cplusPermMulti,
        UserMobileAppDesktopPortalAccess: vmobPermMulti,
        UserTmaPortalAccess: tmaPermMulti,
        UserVanRunsPortalAccess: planPermMulti,
        UserVisionDesktopPortalAccess: 2,
        PortalBranchLimited: branch,
        Admin: admin,
      })

      // matt's jank api forces us to fetch the list, get the reset guid and then make a pw reset call
      const usersRes = await haxios.get(`${process.env.REACT_APP_BASE_URL}/api/users/portal`)
      const user = usersRes.filter((user) => user.UserId === uuid).pop()

      await haxios.post(`${process.env.REACT_APP_BASE_URL}/api/authenticate/reset-password/new-password/`, {
        ResetGuid: user.PasswordResetGuid,
        Password: password
      })
    } catch (e) {
      setLoading(false)
      setError(e?.response?.data?.Message || e?.response?.data || 'Unknown error')
      return
    }

    await refreshList()
    modal.remove()
  }

  return (
    <ModalContainer>
      <Card className="!w-[500px] p-1">
        <Card.Title Icon={PlusIcon}>
          Add user
        </Card.Title>
        <Card.Body>
          {
            error && (
              <div className="p-2 mt-1 mb-1 text-center text-red-800 bg-red-200 rounded-md border border-red-500">{error}</div>
            )
          }
          <div className="flex mt-1 space-x-2">
            <Input label="First name" value={fname} onChange={(e) => setFname(e.target.value)} />
            <Input label="Last name" value={lname} onChange={(e) => setLname(e.target.value)} />
          </div>
          <div className="flex mt-1 space-x-2">
            <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
            <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="flex mt-1 space-x-2">
            <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className="w-full"></div>
          </div>
          <div className="flex mt-3 space-x-2">
            <div className="w-full">
              <div className="text-sm font-bold">TMA Permissions</div>
              <MultiButton
                value={tmaPermMulti}
                options={permMultiOpt}
                onChange={setTmaPermMulti}
              />
            </div>
            <div className="w-full">
              <div className="text-sm font-bold">Vision Mobile Permissions</div>
              <MultiButton
                value={vmobPermMulti}
                options={permMultiOpt}
                onChange={setVmobPermMulti}
              />
            </div>
          </div>
          <div className="flex mt-3 space-x-2">
            <div className="w-full">
              <div className="text-sm font-bold">ConnectPlus Permissions</div>
              <MultiButton
                value={cplusPermMulti}
                options={permMultiOpt}
                onChange={setCplusPermMulti}
              />
            </div>
            <div className="w-full">
              <div className="text-sm font-bold">Delivery Planner Permissions</div>
              <MultiButton
                value={planPermMulti}
                options={permMultiOpt}
                onChange={setPlanPermMulti}
              />
            </div>
          </div>
          <div className="flex mt-3">
            <div className="flex-1">
              <div className="text-sm font-bold">Branch restriction</div>
              <BranchDropdown value={branch} onChange={(opt) => setBranch(opt.value)} />
            </div>
            <div className="flex flex-col items-center ml-2">
              <div className="text-sm font-bold">Admin</div>
              <Checkbox checked={admin} onClick={() => setAdmin(!admin)} />
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            <Button Icon={XMarkIcon} variant="white" onClick={modal.remove}>Close</Button>
            <Button Icon={PlusIcon} variant="blue" onClick={handleSubmit} loading={isLoading}>Add</Button>
          </div>
        </Card.Body>
      </Card>
    </ModalContainer >
  )
})

const BranchDropdown = ({ value, onChange }) => {
  const [options, setOptions] = useState([])

  const loadOptions = async () => {
    const res = await haxios.get(`${process.env.REACT_APP_BASE_URL}/api/map/branches`)

    setOptions([
      {
        label: 'All',
        value: null
      },
      ...res.map((branch) => ({
        label: `[${branch.Branch}] ${branch.Name}`,
        value: branch.Branch
      }))
    ])
  }

  useEffect(() => {
    loadOptions()
  }, [])

  return (
    <Dropdown
      placeholder="Select branch..."
      options={options}
      value={options.filter((opt) => opt.value === value).pop()}
      onChange={onChange}
    />
  )
}
export default AddUserModal